import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import AuthLayout, { AuthLabel, AuthError, AuthLink, SocialLoginButtons } from '../components/ui/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  const { login, loginWithProvider, resendConfirmationEmail, loading, error } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const needsConfirmation = location.state?.needsConfirmation === true;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(formData.email, formData.password);
    if (result.success) navigate('/dashboard');
  };

  const handleResend = async () => {
    if (!formData.email) return;
    setResending(true);
    await resendConfirmationEmail(formData.email);
    setResending(false);
    setResent(true);
    setTimeout(() => setResent(false), 4000);
  };

  return (
    <AuthLayout
      title="Mirë se vini përsëri"
      subtitle="Hyni në llogarinë tuaj për të vazhduar mësimin."
      footer={
        <>
          {t('Nuk keni llogari?')}{' '}
          <AuthLink to="/register">{t('Regjistrohu falas')}</AuthLink>
        </>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit}>

        {/* Email not confirmed banner */}
        {needsConfirmation && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 flex items-start gap-3">
            <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-xs">
              <p className="font-semibold text-yellow-800 mb-0.5">{t('Email-i nuk është konfirmuar')}</p>
              <p className="text-yellow-700">{t('Shko te email-i yt dhe kliko linkun e konfirmimit.')}</p>
              <div className="mt-1.5">
                {resent ? (
                  <span className="inline-flex items-center gap-1 text-green-700 font-semibold">
                    <CheckCircleIcon className="w-3 h-3" /> {t('Email u ridërgua!')}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending || !formData.email}
                    className="inline-flex items-center gap-1 text-yellow-700 font-semibold underline underline-offset-2 hover:text-yellow-900 disabled:opacity-50"
                  >
                    {resending && <ArrowPathIcon className="w-3 h-3 animate-spin" />}
                    {t('Ridërgo email-in e konfirmimit')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {error && <AuthError>{t(error)}</AuthError>}

        {/* Email */}
        <div className="space-y-1.5">
          <AuthLabel htmlFor="email">Email</AuthLabel>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="filan.fisteku@email.com"
            className="h-11 rounded-xl border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 placeholder:text-zinc-400 text-sm focus:border-zinc-900"
          />
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <AuthLabel htmlFor="password">{t('Fjalëkalimi')}</AuthLabel>
            <AuthLink to="/forgot-password" className="text-xs font-normal text-zinc-500 hover:text-zinc-900 no-underline">
              {t('Keni harruar?')}
            </AuthLink>
          </div>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              className="h-11 rounded-xl border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 placeholder:text-zinc-400 text-sm pr-10 focus:border-zinc-900"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600"
            >
              {showPassword
                ? <EyeSlashIcon className="h-4 w-4" />
                : <EyeIcon className="h-4 w-4" />
              }
            </button>
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 bg-black hover:bg-zinc-800 text-white font-bold rounded-xl text-sm transition-colors"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              {t('Duke hyrë...')}
            </span>
          ) : (
            t('Hyr në llogari')
          )}
        </Button>
        <div className="relative py-1">
          <div className="border-t border-zinc-200 dark:border-white/10" />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-[#0a0a0c] px-3 text-[11px] text-zinc-400">{t('ose vazhdo me')}</span>
        </div>
        <SocialLoginButtons onProvider={loginWithProvider} loading={loading} />
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
